import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { PlannerExperience } from '../../types/planner';
import { alpha, useTheme } from '@mui/material/styles';
import { textScale } from '../../theme/standards';

interface ExperienceCardProps {
  experience: PlannerExperience;
  onToggleSelect?: (id: string) => void;
  onOpenDetails?: (experience: PlannerExperience) => void;
  onFocus?: () => void;
  isRestaurant?: boolean;
  onDeleteActivity?: (id: string) => void;
  deletingActivities?: Record<string, boolean>;
  isAnyOperationInProgress?: boolean;
}

const normalizeImageSrc = (src: string): string => {
  const trimmed = (src || '').trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('data:')) {
    return trimmed;
  }

  try {
    let candidate = trimmed;
    if (candidate.startsWith('//')) {
      candidate = `https:${candidate}`;
    }

    if (/^https?:/i.test(candidate)) {
      const parsed = new URL(candidate);
      return `${parsed.protocol}//${parsed.host}${parsed.pathname}`.toLowerCase();
    }
  } catch (error) {
    // Ignore parse errors and fall back to simple string handling
  }

  const base = trimmed.split('?')[0];
  return base.toLowerCase();
};

const ExperienceCardComponent: React.FC<ExperienceCardProps> = ({
  experience,
  onToggleSelect,
  onOpenDetails,
  onFocus,
  isRestaurant = false,
  onDeleteActivity,
  deletingActivities = {},
  isAnyOperationInProgress = false,
}) => {
  const { id, name, category, timeOfVisit, duration, cost, isSelected, imageUrl, imageUrls, isPreliminary } = experience;
  const theme = useTheme();
  const accentColor = theme.palette.secondary.main;
  const selectedBorder = `2px solid ${accentColor}`;
  const defaultBorder = `1px solid ${alpha(theme.palette.primary.main, 0.12)}`;
  const preliminaryBorder = `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`;
  const [failedGalleryUrls, setFailedGalleryUrls] = React.useState<Record<string, boolean>>({});
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  const gallerySources = React.useMemo(() => {
    const combined = [
      ...(imageUrl ? [imageUrl] : []),
      ...(Array.isArray(imageUrls) ? imageUrls : []),
    ];

    const seen = new Set<string>();
    return combined
      .map(src => (src || '').trim())
      .filter(src => {
        if (!src) return false;
        const lower = src.toLowerCase();
        if (lower.includes('via.placeholder.com')) return false;
        if (lower.includes('placeholder')) return false;
        const normalizedKey = normalizeImageSrc(src);
        if (!normalizedKey) return false;
        if (seen.has(normalizedKey)) return false;
        seen.add(normalizedKey);
        return true;
      })
      .slice(0, 6);
  }, [imageUrl, imageUrls]);

  const gallerySignature = React.useMemo(() => gallerySources.join('|'), [gallerySources]);

  React.useEffect(() => {
    setFailedGalleryUrls({});
    setCurrentImageIndex(0);
  }, [id, gallerySignature]);

  const validGalleryUrls = React.useMemo(
    () => gallerySources.filter(src => !failedGalleryUrls[src]),
    [gallerySources, failedGalleryUrls],
  );

  React.useEffect(() => {
    if (validGalleryUrls.length === 0) {
      if (currentImageIndex !== 0) {
        setCurrentImageIndex(0);
      }
      return;
    }

    if (currentImageIndex >= validGalleryUrls.length) {
      setCurrentImageIndex(validGalleryUrls.length - 1);
    }
  }, [validGalleryUrls, currentImageIndex]);

  const activeImageUrl = validGalleryUrls[currentImageIndex] ?? null;

  const rawCategories = (Array.isArray(category) ? category : category ? [category] : []).filter(Boolean) as string[];
  const toTitleCase = (value: string) =>
    value
      .toLowerCase()
      .split(/\s+/)
      .map(word => (word ? `${word[0].toUpperCase()}${word.slice(1)}` : ''))
      .join(' ');
  const categoryMap = new Map<string, string>();
  rawCategories.forEach(cat => {
    const normalized = cat.trim();
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (!categoryMap.has(key)) {
      categoryMap.set(key, toTitleCase(normalized));
    }
  });
  const formattedCategories = Array.from(categoryMap.values());

  const placeholderPattern = /^(?:to be confirmed|to be determined|tbd|pending(?: confirmation)?|unknown|n\/?a|not available)[.!]?$/i;
  const rawDuration = duration?.trim();
  const displayDuration = rawDuration && !placeholderPattern.test(rawDuration) ? rawDuration : undefined;
  const showDurationSkeleton = Boolean(!isPreliminary && rawDuration && !displayDuration);
  
  const rawCost = cost?.trim();
  const displayCost = rawCost && !placeholderPattern.test(rawCost) ? rawCost : undefined;

  const cardRadius = 8;
  const imageFallbackGradient = `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.7)} 0%, ${alpha(accentColor, 0.55)} 100%)`;

  const categoryPillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    px: { xs: 1, sm: 1.05, md: 1 },
    py: { xs: 0.26, sm: 0.28, md: 0.3 },
    borderRadius: '999px',
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: {
      xs: '0.64rem',
      sm: '0.66rem',
      md: '0.69rem',
      lg: '0.71rem',
      xl: '0.72rem',
    },
    lineHeight: 1.3,
    letterSpacing: '0.04em',
  } as const;

  const metaPillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    px: { xs: 0.95, md: 0.9 },
    py: { xs: 0.32, md: 0.3 },
    borderRadius: '999px',
    backgroundColor: alpha(theme.palette.secondary.main, 0.12),
    color: theme.palette.text.primary,
  } as const;

  const metaIconSx = {
    fontSize: 16,
    color: theme.palette.secondary.main,
  } as const;

  const metaLabelSx = {
    fontWeight: 600,
    fontSize: {
      xs: '0.64rem',
      sm: '0.66rem',
      md: '0.68rem',
      lg: '0.7rem',
    },
    color: theme.palette.text.primary,
  } as const;

  const handleCardClick = () => {
    onOpenDetails?.(experience);
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: `${cardRadius}px`,
        overflow: 'hidden',
        border: isSelected ? selectedBorder : isPreliminary ? preliminaryBorder : defaultBorder,
        boxShadow: isSelected ? '0 24px 48px rgba(15, 23, 42, 0.18)' : '0 16px 36px rgba(15, 23, 42, 0.12)',
        transform: isSelected ? 'translateY(-4px)' : 'none',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
        backgroundColor: theme.palette.background.paper,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 22px 44px rgba(15, 23, 42, 0.16)',
        },
        position: 'relative',
      }}
    >
      <CardActionArea
        sx={{ alignItems: 'stretch', display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'transparent' }}
        onClick={handleCardClick}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: {
              xs: '4 / 3',
              sm: '4 / 3',
              md: '4 / 3',
              lg: '5 / 4',
              xl: '5 / 4',
            },
            overflow: 'hidden',
            background: imageFallbackGradient,
            borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.04)}`,
          }}
        >
          {activeImageUrl ? (
            <Box
              component="img"
              src={activeImageUrl}
              alt={name}
              loading="lazy"
              onError={() => {
                setFailedGalleryUrls(prev => ({ ...prev, [activeImageUrl]: true }));
              }}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transition: 'transform 0.4s ease',
              }}
            />
          ) : (
            <Box sx={{ width: '100%', height: '100%' }} />
          )}

          {validGalleryUrls.length > 1 && (
            <>
              <Box
                component="div"
                role="button"
                tabIndex={0}
                onClick={event => {
                  event.stopPropagation();
                  setCurrentImageIndex(prev => {
                    if (validGalleryUrls.length <= 1) return 0;
                    return prev === 0 ? validGalleryUrls.length - 1 : prev - 1;
                  });
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    setCurrentImageIndex(prev => {
                      if (validGalleryUrls.length <= 1) return 0;
                      return prev === 0 ? validGalleryUrls.length - 1 : prev - 1;
                    });
                  }
                }}
                sx={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: alpha(theme.palette.common.white, 0.85),
                  '&:hover': {
                    bgcolor: theme.palette.common.white,
                    cursor: 'pointer',
                  },
                  width: 32,
                  height: 32,
                  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.16)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <KeyboardArrowLeftRoundedIcon fontSize="small" />
              </Box>

              <Box
                component="div"
                role="button"
                tabIndex={0}
                onClick={event => {
                  event.stopPropagation();
                  setCurrentImageIndex(prev => {
                    if (validGalleryUrls.length <= 1) return 0;
                    return prev === validGalleryUrls.length - 1 ? 0 : prev + 1;
                  });
                }}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    setCurrentImageIndex(prev => {
                      if (validGalleryUrls.length <= 1) return 0;
                      return prev === validGalleryUrls.length - 1 ? 0 : prev + 1;
                    });
                  }
                }}
                sx={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: alpha(theme.palette.common.white, 0.85),
                  '&:hover': {
                    bgcolor: theme.palette.common.white,
                    cursor: 'pointer',
                  },
                  width: 32,
                  height: 32,
                  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.16)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <KeyboardArrowRightRoundedIcon fontSize="small" />
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: alpha(theme.palette.common.black, 0.44),
                  color: theme.palette.common.white,
                  borderRadius: '999px',
                  px: 1.25,
                  py: 0.35,
                  fontSize: textScale.support.fontSize,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  userSelect: 'none',
                }}
              >
                {currentImageIndex + 1} / {validGalleryUrls.length}
              </Box>
            </>
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1, width: '100%', p: { xs: 1.85, sm: 2.1, md: 1.9, lg: 1.75 }, display: 'flex', flexDirection: 'column', gap: { xs: 1.2, md: 1 } }}>
          <Stack spacing={{ xs: 0.6, md: 0.5 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: 0.01,
                fontSize: {
                  xs: '0.95rem',
                  sm: '0.98rem',
                  md: '1.01rem',
                  lg: '1.04rem',
                  xl: '1.06rem',
                },
              }}
            >
              {name}
            </Typography>

            {(timeOfVisit || showDurationSkeleton || displayDuration || (isRestaurant && displayCost)) && (
              <Stack direction="row" spacing={{ xs: 0.85, md: 0.75 }} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.4 }}>
                {timeOfVisit && (
                  <Typography
                    variant="body2"
                    sx={{
                      textTransform: 'capitalize',
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      letterSpacing: '0.02em',
                      fontSize: {
                        xs: '0.82rem',
                        sm: '0.84rem',
                        md: '0.86rem',
                        lg: '0.88rem',
                      },
                    }}
                  >
                    {timeOfVisit.replace(/_/g, ' ')}
                  </Typography>
                )}
                {showDurationSkeleton ? (
                  <Skeleton
                    variant="rounded"
                    height={22}
                    width={96}
                    sx={{ borderRadius: '999px' }}
                  />
                ) : (
                  displayDuration && (
                    <Box component="span" sx={metaPillSx}>
                      <AccessTimeRoundedIcon sx={metaIconSx} />
                      <Typography variant="caption" component="span" sx={metaLabelSx}>
                        {displayDuration}
                      </Typography>
                    </Box>
                  )
                )}
                {isRestaurant && displayCost && (
                  <Box component="span" sx={metaPillSx}>
                    <Typography variant="caption" component="span" sx={metaLabelSx}>
                      💰 {displayCost}
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>

          {isPreliminary ? (
            <Stack spacing={1.2} sx={{ width: '100%' }}>
              <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1, maxWidth: 220 }} />
              <Skeleton variant="rectangular" height={16} width={140} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={32} sx={{ borderRadius: 2 }} />
              <Chip
                icon={<HourglassEmptyRoundedIcon sx={{ fontSize: 16 }} />}
                label="Details loading"
                size="small"
                sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
              />
            </Stack>
          ) : (
            <Stack
              spacing={{ xs: 1.2, md: 1 }}
              sx={{
                color: 'text.secondary',
                width: '100%',
                minWidth: 0,
                '& > *': { minWidth: 0 },
              }}
            >
              {formattedCategories.length > 0 && (
                <Stack direction="row" spacing={0.55} sx={{ flexWrap: 'wrap', rowGap: 0.55 }}>
                  {formattedCategories.map(categoryLabel => (
                    <Typography
                      key={categoryLabel}
                      variant="caption"
                      component="span"
                      sx={categoryPillSx}
                    >
                      {categoryLabel}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

// Custom comparison function to prevent re-renders when only object reference changed
const areEqual = (prevProps: ExperienceCardProps, nextProps: ExperienceCardProps) => {
  // Check if the experience data has actually changed
  const prevExp = prevProps.experience;
  const nextExp = nextProps.experience;

  // Compare key properties that would affect rendering
  if (prevExp.id !== nextExp.id) return false;
  if (prevExp.isSelected !== nextExp.isSelected) return false;
  if (prevExp.isPreliminary !== nextExp.isPreliminary) return false;

  // Compare image URLs - these are what cause the flickering
  const prevImages = `${prevExp.imageUrl || ''}|${(prevExp.imageUrls || []).join(',')}`;
  const nextImages = `${nextExp.imageUrl || ''}|${(nextExp.imageUrls || []).join(',')}`;
  if (prevImages !== nextImages) return false;

  // Compare other callback props
  if (prevProps.onToggleSelect !== nextProps.onToggleSelect) return false;
  if (prevProps.onOpenDetails !== nextProps.onOpenDetails) return false;
  if (prevProps.onFocus !== nextProps.onFocus) return false;
  if (prevProps.isRestaurant !== nextProps.isRestaurant) return false;
  if (prevProps.onDeleteActivity !== nextProps.onDeleteActivity) return false;

  const prevDeleting = prevProps.deletingActivities?.[prevExp.id] ?? false;
  const nextDeleting = nextProps.deletingActivities?.[nextExp.id] ?? false;
  if (prevDeleting !== nextDeleting) return false;

  if ((prevProps.isAnyOperationInProgress ?? false) !== (nextProps.isAnyOperationInProgress ?? false)) return false;

  // If all key properties are the same, don't re-render
  return true;
};

// Memoize the component to prevent unnecessary re-renders when props haven't changed
export const ExperienceCard = React.memo(ExperienceCardComponent, areEqual);
